// src/home/components/UrgencyRow.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KpiRow, { KpiCell } from '../../components/common/KpiRow';
import StatTile from '../../components/common/StatTile';

/**
 * UrgencyRow — top-of-dashboard "act now" KPIs.
 *
 * 🔴 W-33 (2026-09-28): der Streifen ist eine Handlungsliste — eine Zahl ohne
 * Ziel ist keine Handlung. Von den sechs Kacheln der Altfassung faellt EINE
 * weg: "Stale targets" — ihre Zahl steht jetzt als Zaehl-Pille im
 * Abschnittskopf "Scraper targets" (`HomeDashboard.jsx`), also genau einmal
 * und am Ort der Handlung (W-33/D2).
 *
 * 🔴 "Price recs" BLEIBT, und das ist eine Entscheidung der Gates zu PR 4
 * (R15-B W1). Das Konzept fuehrt sie als **"Kill (verify first)"** und die
 * Liste "What I could not determine" als **#2 "Confirm before deleting that
 * tile"** (`concepts/dashboard-fulfillment.md`: die Zeile „Kill (verify first)" und die Liste „What I could not determine" Nr. 2 „Confirm before deleting that tile"). Eine Bestaetigung ist
 * nirgends aufgezeichnet, und der Vergleich ist NICHT gefuehrt: die
 * "Pricing automation"-Sektion rechnet ihre Zaehler aus einem ANDEREN Endpunkt
 * (`auto_applied`/`manual_review`/`errors`), waehrend diese Kachel
 * `GET /api/dashboard/kpis.price_recommendations` zeigt
 * (`backend/price_reco_utils.count_price_recommendation_rows`). Eine Streichung
 * ohne diesen Beleg entfernt eine Zahl auf Verdacht — die konservative Fassung
 * (Regel 20 §1) ist die, die nichts bewegt.
 *
 * Jede Kachel traegt ihre Definition als `hint` (sichtbare dritte Zeile), nicht
 * mehr als `labelTooltip`. Ein Tooltip ist hover-only; "was ist das" darf nicht
 * hinter einer Zeigegeste liegen. Tooltips bleiben fuer Formel-Details.
 *
 * 🔴 `animate` ist auf ALLEN Kacheln entfernt (W-33/D4). Die Hochzaehl-Animation
 * war die repo-eigene Regel (pattern-library, KPI rules) und der Mechanismus,
 * der eine Richtungsangabe wieder ausloescht: eine Zahl, die von 0 hochzaehlt,
 * hat keinen ruhigen Ausgangswert, an dem man eine Veraenderung ablesen koennte.
 *
 * 🔴 W-33/D3 (Delta je Kachel) ist NICHT umgesetzt, und das ist gemessen, nicht
 * vergessen: `GET /api/dashboard/kpis` liefert vier ganze Zahlen (dazu seit PR2 die Kachel am Dateiende)
 * (`price_recommendations`, `urgent_reorders`, `po_followups`, `stale_targets`,
 * siehe `backend/dashboard.py:171-176`) und `GET /api/rma/stats` sechs
 * (`backend/rma_routes.py:12426-12433`). Keiner der beiden Abrufe fuehrt eine
 * Vorperiode, eine Reihe oder einen Vormonatswert. Ein `Pill suffix="+12"` waere
 * hier eine ERFUNDENE Zahl. Der einzige echte Delta im Dashboard-Datenbestand
 * ist `storage_summary.sales_change_percent`, und der steht bereits als `hint`
 * an seiner eigenen Zeile.
 *
 * 🔴 P22 (UI2 Teil 10, G15b, 2026-11-03): die Schwere traegt seit dieser Runde
 * ein **benanntes Symbol** im `right`-Slot, nicht mehr nur eine Farbe. Welche
 * Kachel welches traegt, steht an `SeverityMark` am Dateiende. Die Farbe BLEIBT
 * — Farbe plus Symbol ist die staerkere Form, nicht der Ersatz.
 */
export default function UrgencyRow({ kpis, rmaStats, storageSummary, loading = false }) {
  const navigate = useNavigate();

  const overdue = rmaStats?.overdue;
  // 🔴 W-06: no `?? 0` here. A failed fetch must reach `StatTile` as
  // `undefined`, which it already renders as `—`; `?? 0` was the only thing
  // turning "unknown" into a confident zero. The SUM needs its own guard,
  // because `undefined + undefined` is `NaN`, and `NaN ?? '—'` is `NaN`.
  const needsAttention = rmaStats
    ? (rmaStats.needs_action ?? 0) + (rmaStats.inbox_pending ?? 0)
    : undefined;
  const urgentReorders = kpis?.urgent_reorders;
  const priceRecs = kpis?.price_recommendations;
  const openPOs = storageSummary?.open_purchase_orders;

  return (
    // 🔴 R16 W1/W5 (Nachbesserung 2, 2026-10-09): `maxMobile={Infinity}`. Alle
    //    sechs Kacheln tragen ein `onClick` (Drill-in in die gefilterte Liste),
    //    fuenf (bei Bedarf) zusaetzlich eine Statusfarbe — „Open POs" und „New incomplete" waeren
    //    sonst hinter dem Knopf. Die Desktop-Reihenfolge bleibt unveraendert.
    //    Regel und Flaechenliste: `KpiRow.jsx` `@props maxMobile`,
    //    `pattern-library.md` §KPI.
    // 🔴 `data-testid="liste-kpi"` (UI2 Teil 10, G15b, 2026-11-03): die
    //    Hausmarke der KPI-Zone (§15). Sie geht ueber `...rest` auf die
    //    Paper-Wurzel von `KpiRow` (`KpiRow.jsx:265`, `:370`), nicht auf eine
    //    Zelle — die lokalen P22/P10-Proben grenzen damit auf den STREIFEN ein,
    //    und `SEL.kpi` (`design/muster/selektoren.js:201`) findet ihn.
    <KpiRow maxMobile={Infinity} data-testid="liste-kpi">
      <KpiCell>
        {/* 🔴 Ziel war `/rma?status=overdue`. Das ist ein STUMM VERWORFENER
            Parameter, kein Filter: `_VALID_STATUS_FILTER_VALUES` in
            `backend/rma/overview.py:335-344` kennt fuenfzehn Status, `overdue`
            ist keiner davon, und unbekannte Werte werden protokolliert und
            FALLEN GELASSEN (`:495-520`) — die Liste kam damit ungefiltert
            zurueck, also genau die Sorte Zahl, die D5 verbietet. Eine
            gefilterte Fallliste "ueberfaellig" gibt es nicht; die Flaeche, die
            diese Menge zeigt, ist die SLA-Uebersicht (`total_overdue`,
            dieselbe Fallback-Hybrid-Regel wie `overdue_sql`). */}
        <StatTile
          label="Overdue cases"
          value={overdue}
          hint="After-Sales, past SLA"
          loading={loading}
          reserveHint
          onClick={() => navigate('/rma/sla-overview')}
          valueColor={overdue > 0 ? 'error.main' : undefined}
          right={overdue > 0 ? <SeverityMark severity="critical" /> : undefined}
        />
      </KpiCell>
      <KpiCell>
        {/* 🔴 Ziel war `/rma?view=attention`. Diesen Parameter liest NIEMAND:
            `RMAOverviewLayout.jsx:98-184` liest `filter`, `q`, `status`,
            `case_type`, `responsible`, `sort`, `order`, `page` — `view` kommt
            dort nicht vor. `/rma?view=attention` waere also `/rma` mit einem
            toten Parameter daran.

            `/rma` selbst zeigt ALLE Faelle (kein Statusfilter gesetzt), also
            auch geschlossene — der Nenner stimmt nicht. Die drei Status unten
            sind woertlich die Definition von `needs_action` aus
            `backend/rma_routes.py:12403-12406`, und die Liste versteht
            Mehrfachauswahl (`searchParams.getAll('status')`).

            ⚠️ Die Kachel ist eine SUMME (`needs_action` + `inbox_pending`) und
            damit groesser als jede einzelne Liste: die Posteingangs-Haelfte
            lebt in `/rma/inbox`, nicht in der Fallliste. Deshalb bleibt der
            `hint` die Zusammensetzung — die Liste zeigt den groesseren,
            handlungsfaehigen Teil exakt. */}
        <StatTile
          label="Needs attention"
          value={needsAttention}
          hint="After-Sales open + inbox"
          loading={loading}
          reserveHint
          onClick={() => navigate('/rma?status=open&status=approved&status=received')}
          valueColor={needsAttention > 0 ? 'warning.main' : undefined}
          right={needsAttention > 0 ? <SeverityMark severity="warning" /> : undefined}
        />
      </KpiCell>
      <KpiCell>
        {/* 🔴 W1 der Gates zu PR 4: die Kachel war auf eine unbestätigte
            Vermutung gestrichen. Das Konzept nennt sie "Kill (verify first)"
            und fuehrt die fehlende Bestaetigung ausdruecklich als offen
            (`concepts/dashboard-fulfillment.md`: die Zeile „Kill (verify first)" und die Liste „What I could not determine" Nr. 2 „Confirm before deleting that tile"). Solange die
            Verdopplung nicht belegt ist, bleibt die Zahl stehen — eine
            entfernte Kachel ist nicht rueckholbar, eine doppelte Zahl schon.
            Die Definition steht als `hint`, wie bei jeder Kachel hier. */}
        <StatTile
          label="Price recs"
          value={priceRecs}
          hint="Actionable price changes"
          loading={loading}
          reserveHint
          onClick={() => navigate('/products')}
          valueColor={priceRecs > 0 ? 'warning.main' : undefined}
          right={priceRecs > 0 ? <SeverityMark severity="warning" /> : undefined}
        />
      </KpiCell>
      <KpiCell>
        {/* 🔴 Ziel war `/incoming-inventory`. Jene Liste zeigt Ware aus OFFENEN
            Bestellungen und filtert ausgelaufene Produkte bewusst NICHT -- sie ist
            eine Bestandsvorschau, keine Nachbestell-Entscheidung. Seit die Kachel
            ihre Zahl filtert, wurde die Zahl kleiner und jene Liste nicht.

            `/overview` traegt `RecommendedOrdersSection`, gespeist aus
            `GET /api/recommended_purchase_orders`.

            🔴 Was hier gilt, genau: die AUSSCHLUESSE sind deckungsgleich
            (ausgelaufen, abgelehnt, ohne Lieferant, ohne Produkt -- seit dem
            2026-09-19 auch in `dashboard.kpis`). Das FENSTER ist es nicht:
            5 Tage hier, 14 Tage dort. Die Kachel zeigt also eine TEILMENGE der
            Liste, und das ist Absicht -- sie heisst "Urgent".

            Eine fruehere Fassung dieses Kommentars behauptete "dieselbe
            Grundmenge". Das war falsch und stand ungeprueft da; gefunden vom
            Regel-16-Gate. Ein Kommentar, der eine Deckung behauptet, die es
            nicht gibt, ist schlimmer als keiner. */}
        <StatTile
          label="Urgent reorders"
          value={urgentReorders}
          hint="Below reorder point"
          loading={loading}
          reserveHint
          onClick={() => navigate('/overview?section=recommended')}
          valueColor={urgentReorders > 0 ? 'error.main' : undefined}
          right={urgentReorders > 0 ? <SeverityMark severity="critical" /> : undefined}
        />
      </KpiCell>
      <KpiCell>
        {/* 🔴 Ziel war `/overview?status=open`. `POOverview.jsx` liest aus der
            URL nur `section` (`:120`, `:612-613`) — der Statusfilter ist
            LOKALER Zustand (`:157`). Der Parameter waere ein No-Op gewesen.

            🔴 W-10 der Gates zu PR 4: hier stand, der Vorgabefilter der Liste
            sei "woertlich das, was der `hint` dieser Kachel sagt". Das war
            FALSCH, und der `hint` sagte es mit: die Liste filtert auf "alles
            ausser Closed" (`DEFAULT_STATUS_VALUES`, `:103-105`) — also
            INKLUSIVE Draft —, waehrend die Zahl dieser Kachel aus
            `storage_summary.open_purchase_orders` kommt und im Backend
            `status NOT IN ('Draft','Done','Closed','Cancelled')` zaehlt
            (`backend/dashboard.py:355-359`), also OHNE Draft. Die Kachel ist
            damit eine TEILMENGE der Liste, nicht dieselbe Menge. Der `hint`
            nennt jetzt die Menge, die gezaehlt wird. */}
        <StatTile
          label="Open POs"
          value={openPOs}
          hint="Order placed → In delivery"
          loading={loading}
          reserveHint
          onClick={() => navigate('/overview')}
        />
      </KpiCell>
      <KpiCell>
        {/* PR2 PO-Schnellweg (2026-10-27). Owner: „Neben dem Morgen-Briefing
            sollte es auch eine KPI Card dazu auf dem Dashboard geben.“ Die
            sechste Kachel; sie traegt wie alle ein `onClick`. Logik und
            Begruendung in `neueProdukteKachel` am Dateiende — dort, damit kein
            dokumentierter `UrgencyRow.jsx:NNN`-Verweis oberhalb wandert. */}
        <StatTile {...neueProdukteKachel({ kpis, loading, navigate })} />
      </KpiCell>
    </KpiRow>
  );
}

/**
 * Die Kachel „New incomplete“ — voller Name „New products incomplete“ (PR2
 * PO-Schnellweg, 2026-10-27).
 *
 * Die Zahl ist die Kopfzahl von `neue_unvollstaendige`
 * (`backend/produkt_vervollstaendigung/neue_unvollstaendige.py`): angelegt in
 * den letzten 14 Tagen, nicht archiviert, nach Abzug der
 * Lieferanten-Artikelnummer, die heute nichts fuellen kann. DIESELBE Funktion
 * speist das Morgen-Briefing — und der Klick fuehrt auf GENAU diese Menge
 * (`/products/completion?neu=1`, Server-Filter `neu=true`).
 *
 * Drei Zustaende (Plan Punkt 5):
 *   * laden — die Kachel skeletonisiert sich selbst (`loading`),
 *   * Fehler — BENANNT, nie `0` (Rev. 2 W6): das Feld ist `null` und
 *     `kpis.neue_produkte_fehler` ist `true` (eindeutiger Schluessel, R15 W-6),
 *     oder ein aelterer Server liefert es gar nicht; der Wert bleibt
 *     `undefined`, `StatTile` zeichnet „—“,
 *   * `0` — positiv formuliert, nicht als Alarm — 🔴 aber NUR, wenn auch
 *     `nicht_gemessen` und `wartet_auf_katalog` 0 sind (R16 B1). Ein in der PO
 *     gerade angelegtes Produkt ohne `product_completion`-Zeile (Hook laeuft
 *     noch oder ist gescheitert) ist `count 0, nicht_gemessen 1`; „All
 *     complete“ waere dann eine Behauptung ueber Ungemessenes (Regel 22:
 *     unbekannt ist nie vollstaendig). Dann „None open · N not measured“ bzw.
 *     „None open · M await vendor catalog“ (bei 1: „awaits“), beides, wenn
 *     beides zutrifft.
 *
 * 🔴 Etikett KURZ (R16 W7): `KPI_LABEL_SX` haelt ab `sm` einzeilig mit
 * Ellipsis, und bei sechs Kacheln in einer Zeile (1280 px) bleiben rund 130 px —
 * „New products incomplete“ waere gekuerzt und der Volltext unerreichbar. Der
 * volle Name steht im `labelTooltip` (Zeiger) und im `ariaLabel` des Werts
 * (Bildschirmleser hoeren sonst nur das Kurzetikett).
 *
 * Seit dieser Kachel stehen SECHS im Streifen; alle tragen ein `onClick`
 * (`maxMobile={Infinity}` oben gilt damit unveraendert).
 */
function neueProdukteKachel({ kpis, loading, navigate }) {
  const daten = kpis?.neue_produkte_unvollstaendig;
  const fehler = Boolean(kpis?.neue_produkte_fehler) || Boolean(kpis && !daten);
  let hint;
  if (fehler) hint = 'Could not be determined';
  else if (daten && daten.count === 0) hint = nullHinweis(daten);
  else if (daten) hint = `Last ${daten.tage} days${daten.davon_po ? ` · ${daten.davon_po} from POs` : ''}`;
  const wert = daten ? daten.count : undefined;
  return {
    label: 'New incomplete',
    labelTooltip: daten?.tage
      ? `New products incomplete · created in the last ${daten.tage} days`
      : 'New products incomplete',
    ariaLabel: 'New products incomplete',
    value: wert,
    hint,
    loading,
    reserveHint: true,
    onClick: () => navigate('/products/completion?neu=1'),
    valueColor: wert > 0 ? 'warning.main' : undefined,
    right: wert > 0 ? <SeverityMark severity="warning" /> : undefined,
  };
}

/**
 * `SeverityMark` — die Schwere als **benanntes Symbol** (P22, UI2 Teil 10 G15b,
 * 2026-11-03).
 *
 * 🔴 Bis G15b trug der Streifen die Schwere **nur** als Farbe (`valueColor`).
 * Wer Farben nicht unterscheidet, sah „3" und „0" gleich. Das Symbol traegt die
 * Schwere zusaetzlich, und `role="img"` + `aria-label` gibt ihr einen Namen —
 * ein nacktes `<svg>` mit `aria-label` wird von Hilfsmitteln uebersprungen
 * (Hausform: `ops/DepHealth.jsx:109`).
 *
 * 🔴 Die zwei Namen sind `Critical`/`Warning`, **nicht** die Metrik: die Kachel
 * heisst bereits „Overdue cases"; ein zweites „Overdue" ergaebe
 * „Overdue cases, 3, Overdue, button" und wuerde doppelt vorgelesen.
 *
 * 🔴 Der Name landet NICHT im zugaenglichen Namen des Klickziels: `StatTile`
 * setzt `aria-label` auf das ETIKETT, wenn der Wert eine Zahl ist
 * (`StatTile.jsx:346-348`; den Wert liest es bewusst NICHT vor, Begruendung
 * `:333-345`), und dieser Slot steht als Geschwister daneben, nicht darin.
 * Deshalb bleibt `getByRole('button', { name: 'Needs attention' })` woertlich
 * richtig (`tests/rma/faelle-deeplinks.spec.ts:54`, am Kode nachgemessen
 * 2026-11-03).
 *
 * Nur bei `wert > 0` gerendert: eine 0 ist neutral und traegt keine Schwere,
 * und ein unbekannter Wert (`undefined`) ist keine Null.
 */
function SeverityMark({ severity }) {
  const kritisch = severity === 'critical';
  const Icon = kritisch ? ErrorOutlineIcon : WarningAmberIcon;
  return (
    <Box
      component="span"
      role="img"
      aria-label={kritisch ? 'Critical' : 'Warning'}
      sx={{ display: 'inline-flex', color: kritisch ? 'error.main' : 'warning.main' }}
    >
      <Icon fontSize="small" />
    </Box>
  );
}

/**
 * Der Hinweis bei `count 0` (R16 B1): positiv NUR, wenn nichts ungemessen ist
 * und nichts auf den Lieferanten-Katalog wartet. Einzahl/Mehrzahl (R16 Runde 2
 * H-a): „1 awaits“, „2 await“. Die Kachel bleibt ein Bruchstueck wie ihre
 * Nachbarn („Last 14 days · 2 from POs“); ganze Saetze stehen in der Liste.
 */
function nullHinweis(daten) {
  const teile = [];
  if (daten.nicht_gemessen > 0) teile.push(`${daten.nicht_gemessen} not measured`);
  if (daten.wartet_auf_katalog > 0) {
    teile.push(`${daten.wartet_auf_katalog} ${daten.wartet_auf_katalog === 1 ? 'awaits' : 'await'} vendor catalog`);
  }
  return teile.length ? `None open · ${teile.join(' · ')}` : `All complete · last ${daten.tage} days`;
}
